import { defineMessages } from 'react-intl';

import config from '@plone/volto/registry';

const messages = defineMessages({
  blockTypeField: {
    id: 'blockTypeField',
    defaultMessage: 'Type',
  },
  sourceField: {
    id: 'sourceField',
    defaultMessage: 'Source',
  },
  fieldsetBlockSettings: {
    id: 'fieldsetBlockSettings',
    defaultMessage: '{blockType} settings',
  },
  fieldsetBlockSettingsUnnamed: {
    id: 'fieldsetBlockSettingsUnnamed',
    defaultMessage: 'Block settings',
  },
  fieldsetSourceSettings: {
    id: 'fieldsetSourceSettings',
    defaultMessage: 'Source settings',
  },
});

export function repeaterBlockSchema({ formData, intl }) {
  const fields = ['blockType'];
  const fieldsets = [
    {
      id: 'default',
      title: 'Default',
      fields: fields,
    },
  ];

  /** @type {Array<string>} */
  const allowedBlocks = config.settings['volto-repeater-block'].allowedBlocks;
  const selectableBlocks = new Map();
  Object.values(config.blocks.blocksConfig || {}).forEach((blockConfig) => {
    if (allowedBlocks.includes(blockConfig?.id)) {
      selectableBlocks.set(blockConfig?.id, blockConfig?.title);
    }
  });

  const sources = config.settings['volto-repeater-block']?.sources ?? {};
  if (Object.keys(sources).length > 0) {
    fields.push('source');
  }

  const properties = {
    blockType: {
      title: intl.formatMessage(messages.blockTypeField),
      choices: Array.from(
        selectableBlocks.entries(),
        ([blockId, blockTitle]) => {
          return [blockId, blockTitle];
        },
      ),
      noValueOption: false,
    },
    source: {
      title: intl.formatMessage(messages.sourceField),
      choices: Object.entries(sources).map(([sourceKey, sourceSettings]) => {
        return [sourceKey, sourceSettings.title];
      }),
      noValueOption: false,
    },
  };
  if (!!formData?.blockType) {
    const blockType = config.blocks.blocksConfig[formData.blockType];
    const blockSchema =
      typeof blockType.blockSchema === 'function'
        ? blockType.blockSchema({ formData, intl })
        : blockType.blockSchema;
    if (blockSchema) {
      const blockSchemaDefaultFieldsetIndex = blockSchema.fieldsets.findIndex(
        (fieldset) => fieldset.id === 'default',
      );
      const blockFieldset =
        blockSchema.fieldsets[blockSchemaDefaultFieldsetIndex];

      // Copy the blocks schema across as "Block settings" to the repeater block
      blockFieldset.id = 'fieldsetBlockSettings';
      blockFieldset.title = blockType.title
        ? intl.formatMessage(messages.fieldsetBlockSettings, {
            blockType: blockType?.title || formData.blockType,
          })
        : intl.formatMessage(messages.fieldsetBlockSettingsUnnamed);
      blockFieldset.fields = blockFieldset.fields.reduce(
        (unmappedFields, fieldId) => {
          if (!formData.source) {
            unmappedFields.push(`block_${fieldId}`);
          } else if (
            !Object.keys(
              config.settings['volto-repeater-block'].mappings[
                formData.blockType
              ]?.[formData.source] || {},
            ).includes(fieldId)
          ) {
            unmappedFields.push(`block_${fieldId}`);
          }
          return unmappedFields;
        },
        [],
      );

      // Append `block_` to each of the block fields so they don't conflict with the repeater block
      blockSchema.properties = Object.fromEntries(
        Object.entries(blockSchema.properties).map(
          ([fieldId, fieldSettings]) => {
            return [`block_${fieldId}`, fieldSettings];
          },
        ),
      );
      Object.assign(properties, blockSchema.properties);

      fieldsets.push(blockFieldset);
    } else {
      console.error(`No block schema found for ${formData.blockType}`);
    }
  }

  if (!!formData?.source) {
    const source = sources[formData.source];
    const sourceSchema =
      typeof source.schema === 'function'
        ? source.schema({ formData, intl })
        : source.schema;

    const sourceSchemaDefaultFieldsetIndex = sourceSchema.fieldsets.findIndex(
      (fieldset) => fieldset.id === 'default',
    );
    const sourceFieldset = {
      id: 'fieldsetSourceSettings',
      title: intl.formatMessage(messages.fieldsetSourceSettings),
      fields: sourceSchema.fieldsets[
        sourceSchemaDefaultFieldsetIndex
      ].fields.map((fieldId) => `source_${fieldId}`),
    };

    // Append `source_` to each of the source fields so they don't conflict with the repeater block
    sourceSchema.properties = Object.fromEntries(
      Object.entries(sourceSchema.properties).map(
        ([fieldId, fieldSettings]) => {
          return [`source_${fieldId}`, fieldSettings];
        },
      ),
    );
    Object.assign(properties, sourceSchema.properties);

    fieldsets.push(sourceFieldset);
  }

  return {
    required: [],
    fieldsets: fieldsets,
    properties: properties,
  };
}
